import { useState, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Link } from "react-router-dom";

const faqData = [
  {
    category: "General",
    question: "What is a CCTV camera?",
    answer:
      "CCTV (Closed-Circuit Television) is a video surveillance system used to monitor and record activities in homes, offices, shops, and public areas for security purposes.",
  },
  {
    category: "General",
    question: "What is the difference between DVR and NVR?",
    answer:
      "DVR works with analog cameras, while NVR works with IP cameras. NVR systems provide better image quality and advanced features.",
  },
  {
    category: "Installation",
    question: "Do you provide CCTV installation services?",
    answer:
      "Yes, we provide professional installation services for residential, commercial, and industrial properties.",
  },
  {
    category: "Installation",
    question: "How long does installation take?",
    answer:
      "Installation time depends on the number of cameras. Typically, 2–4 cameras take 2–4 hours, while larger setups may take a full day.",
  },
  {
    category: "Technical",
    question: "Can I view CCTV cameras on my mobile?",
    answer:
      "Yes, you can monitor live footage and playback recordings through Android and iOS apps from anywhere with internet access.",
  },
  {
    category: "Technical",
    question: "Do CCTV cameras work at night?",
    answer:
      "Yes, most CCTV cameras include infrared (IR) night vision or full-color night vision for clear footage in low-light conditions.",
  },
  {
    category: "Pricing",
    question: "How much does CCTV installation cost?",
    answer:
      "The cost depends on the number of cameras, storage capacity, wiring requirements, and brand selection. Contact us for a customized quote.",
  },
  {
    category: "Pricing",
    question: "Do you offer warranty and support?",
    answer:
      "Yes, we provide standard warranty coverage and ongoing technical support services.",
  },
];

const categories = ["All", "General", "Installation", "Technical", "Pricing"];

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [openIndex, setOpenIndex] = useState(null);

  const filteredFAQs = useMemo(() => {
    return faqData.filter((faq) => {
      const matchesCategory =
        activeCategory === "All" || faq.category === activeCategory;

      const matchesSearch =
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchTerm]);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      
      {/* Hero Section */}
      <section className="bg-blue-900 text-white py-20 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Frequently Asked Questions
        </h1>
        <p className="max-w-2xl mx-auto text-gray-200">
          Find answers to common questions about our CCTV systems, installation
          services, pricing, and technical support.
        </p>
      </section>

      {/* Main Content */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-16">
        
        {/* Search Bar */}
        <div className="relative max-w-xl mx-auto mb-10">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search your question..."
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setActiveCategory(cat);
                setOpenIndex(null);
              }}
              className={`px-5 py-2 rounded-full text-sm font-medium transition ${
                activeCategory === cat
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto space-y-4">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex justify-between items-center p-6 text-left font-semibold text-gray-800 hover:bg-gray-50 transition"
                >
                  {faq.question}
                  <ChevronDown
                    className={`w-5 h-5 transition-transform duration-300 ${
                      openIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    openIndex === index
                      ? "grid-rows-[1fr] opacity-100 p-6 pt-0"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500">
              No matching questions found.
            </p>
          )}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Still Have Questions?
          </h3>
          <p className="text-gray-600 mb-6">
            Contact our support team for expert assistance and personalized CCTV solutions.
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-medium transition">
         <Link to="/contact">Contact Support</Link>
          </button>
        </div>

      </section>
    </div>
  );
}